import type {ComponentType} from 'react'
import {useReducer} from 'react'
import type {NavigateFunction} from 'react-router'
import {useNavigate, Navigate} from 'react-router'
import type {Moment} from 'moment-timezone'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks'
import type {DateOption} from '@freckle/educator-entities/ts/common/helpers/date-options'
import type {LangT} from '@freckle/educator-entities/ts/common/helpers/languages'
import type {GenericAssignmentSessionAttrsT} from '@freckle/educator-entities/ts/common/models/generic-assignment-session'
import {spanDangerous} from '@freckle/educator-entities/ts/common/models/html-text'
import type {PremiumLicensesAttrs} from '@freckle/educator-entities/ts/common/models/premium-licenses'
import type {FactPracticeCourseLevelsAttrs} from '@freckle/educator-entities/ts/math/fact-practice/models/math-fact-practice-course-levels'
import type {MathFactPracticeOperationT} from '@freckle/educator-entities/ts/math/fact-practice/models/math-fact-practice-operation'
import type {StudentAttrs} from '@freckle/educator-entities/ts/users/models/student'
import {Tabs, Tab} from '@freckle/educator-materials/ts/components/tabs'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {CourseSelectorHandler} from '@freckle/classroom/ts/common/components/course-selector/course-selector-handler'
import {PageWrapper} from '@freckle/classroom/ts/common/components/page-wrapper/index'
import type {FactPracticeReportTabNameT} from '@freckle/classroom/ts/common/routers/routes'
import {Routes} from '@freckle/classroom/ts/common/routers/routes'
import {useQueryParamAsString} from '@freckle/classroom/ts/hooks/use-params'
import {FactMasterySummary} from './fact-mastery-summary'
import {FactPracticeSessionHistory} from './fact-practice-session-history'

type Props = {
  genericSessions: Array<GenericAssignmentSessionAttrsT>
  students: Array<StudentAttrs>
  mathFactPracticeCourseLevels: Array<FactPracticeCourseLevelsAttrs>
  dateOption: DateOption
  from: Moment
  to: Moment
  isPremium: boolean
  initialStudentId: number | null | undefined
  premiumLicenses: PremiumLicensesAttrs
  allMathFactPracticeOperations: Array<MathFactPracticeOperationT>
  language: LangT
}

type State = {
  redirectToFactPracticeAssign: boolean
  key: string
}

type Message = {
  redirectToFactPracticeAssign?: boolean
  key?: string
}

type Dispatcher = (message: Message) => void

function reducer(state: State, message: Message): State {
  return {...state, ...message}
}

function useStateManagement(props: {activeTab: FactPracticeReportTabNameT}): [State, Dispatcher] {
  const initialState: State = {
    redirectToFactPracticeAssign: false,
    key: props.activeTab,
  }

  return useReducer(reducer, initialState)
}

function getNavigateFactPracticeAssign(dispatcher: Dispatcher): () => void {
  return () => {
    dispatcher({redirectToFactPracticeAssign: true})
  }
}

function getHandleSelect(dispatcher: Dispatcher): (key: string) => void {
  return (key: string) => {
    dispatcher({key})
  }
}

export const FactPracticeReports: ComponentType<Props> = (props: Props) => {
  const {
    genericSessions,
    students,
    mathFactPracticeCourseLevels,
    isPremium,
    premiumLicenses,
    dateOption,
    from,
    to,
    initialStudentId,
    allMathFactPracticeOperations,
    language,
  } = props

  const assignedTabFromQueryParam = useQueryParamAsString('tab')
  const activeTab = getActiveTab(assignedTabFromQueryParam)
  const [state, dispatch] = useStateManagement({activeTab})
  const {redirectToFactPracticeAssign, key} = state
  const {t} = useSafeTranslation()
  const navigate = useNavigate()

  useSafeEffectExtraDeps<{navigate: NavigateFunction}>(
    ({navigate}) => {
      const searchParams = new URLSearchParams(window.location.search)
      searchParams.set('tab', key)
      navigate({pathname: location.pathname, search: `?${searchParams}`}, {replace: true})
    },
    [key],
    {
      navigate: {value: navigate, comparator: () => true},
    },
  )

  const navigateFactPracticeAssign = getNavigateFactPracticeAssign(dispatch)
  const handleSelect = getHandleSelect(dispatch)

  if (redirectToFactPracticeAssign) {
    return <Navigate to={Routes.math.assignFactPractice()} />
  }

  const courseSelector = <CourseSelectorHandler withLabel />

  const breadcrumbsConfig = {
    path: [
      {
        url: Routes.reportsOverview('math'),
        name: t('BREADCRUMB_REPORT_OVERVIEW_NAME'),
      },
    ],
    currentItemName: spanDangerous(t('FACT_PRACTICE_REPORT_PAGE_TITLE')),
  }

  return (
    <PageWrapper
      pageHeaderText={t('FACT_PRACTICE_REPORT_PAGE_TITLE')}
      breadcrumbsConfig={breadcrumbsConfig}
    >
      <>
        <div className="row std-margin-bottom-md">
          <div className="col-3">{courseSelector}</div>
        </div>
        <Tabs
          id="fact-practice-reports-tabs"
          activeKey={key}
          onSelect={t => (t !== null ? handleSelect(t) : null)}
          expandTabs
        >
          <Tab eventKey="activity" title={t('FACT_PRATICE_REPORT_TAB_ACTIVITY')}>
            <FactPracticeSessionHistory
              students={students}
              genericSessions={genericSessions}
              dateOption={dateOption}
              from={from}
              to={to}
              isPremium={isPremium}
              navigateFactPracticeAssign={navigateFactPracticeAssign}
              initiallyExpandedRowStudentId={initialStudentId}
              allMathFactPracticeOperations={allMathFactPracticeOperations}
            />
          </Tab>
          <Tab eventKey="basic-operations" title={t('FACT_PRACTICE_REPORT_BASIC_OPERATIONS')}>
            <FactMasterySummary
              mathFactPracticeCourseLevels={mathFactPracticeCourseLevels}
              students={students}
              isPremium={isPremium}
              premiumLicenses={premiumLicenses}
              navigateFactPracticeAssign={navigateFactPracticeAssign}
              language={language}
              activeTab={key as FactPracticeReportTabNameT}
            />
          </Tab>
          <Tab
            eventKey="integers-and-fractions"
            title={t('FACT_PRACTICE_REPORT_INTEGERS_AND_FRACTIONS')}
          >
            <FactMasterySummary
              mathFactPracticeCourseLevels={mathFactPracticeCourseLevels}
              students={students}
              isPremium={isPremium}
              premiumLicenses={premiumLicenses}
              navigateFactPracticeAssign={navigateFactPracticeAssign}
              language={language}
              activeTab={key as FactPracticeReportTabNameT}
            />
          </Tab>
        </Tabs>
      </>
    </PageWrapper>
  )
}

function getActiveTab(
  queryParam: FactPracticeReportTabNameT | string | null | undefined,
): FactPracticeReportTabNameT {
  switch (queryParam) {
    case 'basic-operations':
    case 'integers-and-fractions':
      return queryParam
    default:
      return 'activity'
  }
}
